import { Link } from "react-router-dom";
import { Heart, ShoppingBag } from "lucide-react";
import { CardArt } from "./CardArt";
import { useCart, useWishlist } from "../lib/store";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (url) => (!url ? null : url.startsWith("http") ? url : `${BACKEND}${url}`);

const langMap = { jp: "🇯🇵 JP", en: "🇬🇧 EN", it: "🇮🇹 IT", other: "🌐" };

const Badge = ({ label }) => {
  const styles = {
    PREORDER: "text-[#D97706] border-[#D97706]",
    ALLOCATION: "text-[#F3D068] border-[#F3D068] bg-[#C9A227]/10",
    NEW: "text-[#60A5FA] border-[#60A5FA]",
    LIMITED: "text-[#C9A227] border-[#C9A227]",
    HOT: "text-[#F87171] border-[#F87171]",
    "SOLD OUT": "text-[#DC2626] border-[#DC2626]",
  };
  return <span className={`ps-badge ${styles[label] || "text-white border-white/30"}`}>{label}</span>;
};

export const ProductCard = ({ product }) => {
  const { add } = useCart();
  const { toggle, has } = useWishlist();
  const soldOut = product.stock === 0 && !product.is_preorder;
  const badges = [...(product.badges || [])];
  if (soldOut && !badges.includes("SOLD OUT")) badges.push("SOLD OUT");

  const onAdd = (e) => {
    e.preventDefault();
    if (soldOut) return;
    add(product, 1);
    toast.success("Aggiunto al carrello", { description: product.name });
  };

  const onWish = (e) => {
    e.preventDefault();
    toggle(product.product_id);
    toast(has(product.product_id) ? "Rimosso dalla wishlist" : "Aggiunto alla wishlist");
  };

  return (
    <Link
      to={`/prodotto/${product.slug}`}
      data-testid={`product-card-${product.slug}`}
      className="card-tcg group relative flex flex-col overflow-hidden rounded-sm"
    >
      <div className="relative">
        {product.image_url ? (
          <div className="relative w-full h-56 bg-[#0b0b0d] border border-[#C9A227]/25 overflow-hidden">
            <img src={resolveImg(product.image_url)} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
          </div>
        ) : (
          <CardArt product={product} size="md" />
        )}

        <button
          onClick={onWish}
          data-testid={`wishlist-toggle-${product.slug}`}
          aria-label="Wishlist"
          className="absolute top-3 right-3 h-9 w-9 flex items-center justify-center bg-black/70 border border-[#C9A227]/30 hover:border-[#C9A227] transition-colors rounded-sm"
        >
          <Heart size={16} className={has(product.product_id) ? "fill-[#C9A227] text-[#C9A227]" : "text-[#C9A227]"} />
        </button>

        {badges.length > 0 && (
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {badges.map((b) => <Badge key={b} label={b} />)}
          </div>
        )}
      </div>

      <div className="p-4 flex flex-col gap-3 flex-1">
        <div className="flex items-center gap-2 text-[10px] font-mono-ps text-[#9CA3AF] tracking-widest uppercase">
          <span>{langMap[product.language] || "TCG"}</span>
          <span className="text-[#C9A227]/40">•</span>
          <span>{(product.category || "").replace(/-/g, " ")}</span>
        </div>
        <h3 className="font-display font-semibold text-white leading-tight line-clamp-2 group-hover:text-[#F3D068] transition-colors">
          {product.name}
        </h3>

        {product.delivery_time && (
          <div className="text-[10px] font-mono-ps text-[#C9A227]/80 tracking-widest uppercase flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-[#C9A227]" />
            {product.delivery_time}
          </div>
        )}

        <div className="mt-auto flex items-end justify-between pt-2">
          <div>
            {product.old_price && (
              <div className="text-xs text-[#9CA3AF] line-through">€ {product.old_price.toFixed(2)}</div>
            )}
            {product.preorder_type === "allocation-deposit" && product.deposit_percent ? (
              <>
                <div className="text-[10px] font-mono-ps text-[#9CA3AF] uppercase tracking-widest">Acconto {product.deposit_percent}%</div>
                <div className="text-xl font-display font-bold text-[#C9A227] font-mono-ps">
                  € {((product.price * product.deposit_percent) / 100).toFixed(2)}
                </div>
                <div className="text-[10px] text-[#9CA3AF]">totale € {product.price.toFixed(2)}</div>
              </>
            ) : (
              <div className="text-xl font-display font-bold text-[#C9A227] font-mono-ps">
                € {product.price.toFixed(2)}
              </div>
            )}
          </div>
          <button
            onClick={onAdd}
            disabled={soldOut}
            data-testid={`add-to-cart-${product.slug}`}
            className="h-10 px-3 flex items-center gap-2 border border-[#C9A227]/50 text-[#C9A227] hover:bg-[#C9A227] hover:text-black transition-all rounded-sm text-xs font-bold tracking-wider uppercase disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ShoppingBag size={14} />
            {soldOut ? "Esaurito" : "Cart"}
          </button>
        </div>
      </div>
    </Link>
  );
};
